/**
 * Servicio para gestión de servicios a través de la nueva API
 * Centraliza todas las operaciones de servicios con la API backend
 */

import { apiHelpers } from './normalizedApiClient';
import { normalizeDataArray, normalizeDataObject } from '../utils/normalizationUtils';
import { API_ENDPOINTS } from '../config/api';
import { servicioTransforms } from '../utils/dataTransforms';

/**
 * Obtiene todos los servicios
 * @param {number} sucursalId - ID de la sucursal (requerido)
 * @param {Object} filtros - Filtros opcionales (activo, categoria_id, etc.)
 * @returns {Promise<Array>} - Lista de servicios
 */
export const getServicios = async (sucursalId, filtros = {}) => {
  try {
    //console.log('🔧 [ServiciosService] Obteniendo datos');

    
    if (!sucursalId) {

      return [];
    }
    
    const params = {
      sucursal_id: sucursalId,
      ...filtros
    };
    

    
    const response = await apiHelpers.servicios.get(API_ENDPOINTS.SERVICIOS.BASE, { params });

    
    if (response.data) {
      // El backend ahora devuelve directamente el array
      const serviciosData = Array.isArray(response.data) ? response.data : [];

      return servicioTransforms.fromApiList(serviciosData);
    }
    return [];
  } catch (error) {
    console.error('❌ [ServiciosService] Error al obtener servicios:', error);
    
    // Si hay un error 500 del servidor, devolver array vacío en lugar de fallar
    if (error.response?.status === 500) {
      console.warn('⚠️ [ServiciosService] Fallback: Devolviendo array vacío debido a error 500');
      return [];
    }
    
    throw error;
  }
};

/**
 * Crea un nuevo servicio
 * @param {Object} servicio - Datos del servicio
 * @returns {Promise<Object>} - Servicio creado
 */
export const createServicio = async (servicio) => {
  try {
    //console.log('🔧 [ServiciosService] Creando registro');

    
    const servicioData = servicioTransforms.toApi(servicio);
    const response = await apiHelpers.servicios.post(API_ENDPOINTS.SERVICIOS.BASE, servicioData);
    
    if (response.data) {
      const servicioResponse = response.data.data || response.data.servicio || response.data;

      return servicioTransforms.fromApi(servicioResponse);
    }
    
    throw new Error('Respuesta inválida del servidor');
  } catch (error) {
    console.error('❌ [ServiciosService] Error al crear servicio:', error);
    throw error;
  }
};

/**
 * Actualiza un servicio existente
 * @param {string|number} id - ID del servicio
 * @param {Object} servicio - Datos actualizados
 * @returns {Promise<Object>} - Servicio actualizado
 */
export const updateServicio = async (id, servicio) => {
  try {
    //console.log('🔧 [ServiciosService] Actualizando registro');

    
    const servicioData = servicioTransforms.toApi(servicio);
    const response = await apiHelpers.servicios.put(API_ENDPOINTS.SERVICIOS.BY_ID(id), servicioData);
    
    if (response.data) {
      const servicioResponse = response.data.data || response.data.servicio || response.data;

      return servicioTransforms.fromApi(servicioResponse);
    }
    
    throw new Error('Respuesta inválida del servidor');
  } catch (error) {
    console.error('❌ [ServiciosService] Error al actualizar servicio:', error);
    throw error;
  }
};

/**
 * Cambia el estado activo de un servicio
 * @param {string|number} id - ID del servicio
 * @param {boolean} activo - Nuevo estado activo
 * @returns {Promise<Object>} - Servicio actualizado
 */
export const toggleServicioActive = async (id, activo) => {
  try {
    //console.log('🔧 [ServiciosService] Procesando operación');

    
    const response = await apiHelpers.servicios.patch(API_ENDPOINTS.SERVICIOS.BY_ID(id), { activo });
    
    if (response.data) {
      const servicioResponse = response.data.data || response.data.servicio || response.data;

      return servicioTransforms.fromApi(servicioResponse);
    }
    
    throw new Error('Respuesta inválida del servidor');
  } catch (error) {
    console.error('❌ [ServiciosService] Error al cambiar estado del servicio:', error);
    throw error;
  }
};

/**
 * Elimina un servicio (soft delete)
 * @param {string|number} id - ID del servicio
 * @returns {Promise<string>} - ID del servicio eliminado
 */
export const deleteServicio = async (id) => {
  try {
    //console.log('🔧 [ServiciosService] Eliminando registro');

    
    const response = await apiHelpers.servicios.delete(API_ENDPOINTS.SERVICIOS.BY_ID(id));
    
    if (response.data) {

      return id.toString();
    }
    
    throw new Error('Respuesta inválida del servidor');
  } catch (error) {
    console.error('❌ [ServiciosService] Error al eliminar servicio:', error);
    throw error;
  }
};

/**
 * Obtiene un servicio por su ID
 * @param {string|number} id - ID del servicio
 * @returns {Promise<Object|null>} - Servicio encontrado o null
 */
export const getServicioById = async (id) => {
  try {
    //console.log('🔧 [ServiciosService] Obteniendo datos');

    
    const response = await apiHelpers.servicios.get(API_ENDPOINTS.SERVICIOS.BY_ID(id));
    
    if (response.data) {
      const servicioResponse = response.data.data || response.data.servicio || response.data;

      return servicioTransforms.fromApi(servicioResponse);
    }
    
    return null;
  } catch (error) {
    if (error.response?.status === 404) {

      return null;
    }
    console.error('❌ [ServiciosService] Error al obtener servicio:', error);
    throw error;
  }
};

/**
 * Busca servicios por nombre o descripción
 * @param {string} termino - Término de búsqueda
 * @param {number} sucursalId - ID de la sucursal (requerido)
 * @returns {Promise<Array>} - Lista de servicios encontrados
 */
export const buscarServicios = async (termino, sucursalId) => {
  try {
    //console.log('🔧 [ServiciosService] Procesando operación');

    
    if (!sucursalId) {

      return [];
    }
    
    const response = await apiHelpers.servicios.get(API_ENDPOINTS.SERVICIOS.BASE, {
      params: { 
        q: termino,
        sucursal_id: sucursalId
      }
    });
    
    if (response.data) {
      const serviciosData = response.data.data || response.data.servicios || response.data;

      return servicioTransforms.fromApiList(serviciosData);
    }
    return [];
  } catch (error) {
    console.error('❌ [ServiciosService] Error al buscar servicios:', error);
    
    // Si hay un error 500 del servidor, devolver array vacío
    if (error.response?.status === 500) {
      console.warn('⚠️ [ServiciosService] Fallback: Devolviendo array vacío debido a error 500');
      return [];
    }
    
    throw error;
  }
};

/**
 * Obtiene servicios por categoría
 * @param {string} categoriaId - ID de la categoría
 * @param {number} sucursalId - ID de la sucursal (requerido)
 * @returns {Promise<Array>} - Lista de servicios de la categoría
 */
export const getServiciosPorCategoria = async (categoriaId, sucursalId) => {
  try {
    //console.log('🔧 [ServiciosService] Obteniendo datos');
    //console.log(`🔍 [ServiciosService] Obteniendo servicios de categoría "${categoriaId}" en sucursal:`, sucursalId);
    
    if (!sucursalId) {
      console.warn('⚠️ [ServiciosService] sucursalId es requerido para filtrar por categoría');
      return [];
    }
    
    const response = await apiHelpers.servicios.get(API_ENDPOINTS.SERVICIOS.BASE, {
      params: { 
        categoria_id: categoriaId,
        sucursal_id: sucursalId
      }
    });
    
    if (response.data) {
      const serviciosData = response.data.data || response.data.servicios || response.data;
      //console.log(`✅ [ServiciosService] ${serviciosData.length} servicios de categoría obtenidos`);
      return servicioTransforms.fromApiList(serviciosData);
    }
    return [];
  } catch (error) {
    console.error('❌ [ServiciosService] Error al obtener servicios por categoría:', error);
    
    // Si hay un error 500 del servidor, devolver array vacío
    if (error.response?.status === 500) {
      console.warn('⚠️ [ServiciosService] Fallback: Devolviendo array vacío debido a error 500');
      return [];
    }
    
    throw error;
  }
};

/**
 * Obtiene las categorías disponibles para servicios
 * @returns {Promise<Array>} - Lista de categorías únicas
 */
export const getCategoriesServicios = async () => {
  try {
    //console.log('🔧 [ServiciosService] Obteniendo datos');
    //console.log('🔍 [ServiciosService] Obteniendo categorías disponibles...');
    
    const response = await apiHelpers.servicios.get(`${API_ENDPOINTS.SERVICIOS.BASE}/categorias`);
    
    if (response.data) {
      const categoriasData = response.data.data || response.data.categorias || response.data;
      //console.log(`✅ [ServiciosService] ${categoriasData.length} categorías disponibles`);
      return categoriasData;
    }
    return [];
  } catch (error) {
    console.error('❌ [ServiciosService] Error al obtener categorías:', error);
    
    // Si hay un error 500 del servidor, devolver array vacío
    if (error.response?.status === 500) {
      console.warn('⚠️ [ServiciosService] Fallback: Devolviendo array vacío debido a error 500');
      return [];
    }
    
    throw error;
  }
};
